import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import React, {
  useContext,
  type FC,
  memo,
  forwardRef,
  type RefObject,
  type KeyboardEventHandler,
  type ChangeEventHandler,
} from 'react';
import { DeviceTypeContext } from '../../../contexts/DeviceType';
import { PaymentProviderContext } from '../../../contexts/PaymentProvider';
import { type GeneralFieldProps } from '../../../types/Field';
import { FieldName } from '../../../types/CardFieldName';
import { t } from '../../../utils/i18n';
import { formatCardNumber } from '../../CardForm/utils/formatting';
import { FieldErrorMessage } from '../../FieldErrorMessage';
import { FieldInput } from '../../FieldInput';
import { FieldLabel } from '../../FieldLabel';
import { getFieldErrorMessage } from '../../../utils/getFieldErrorMessage';
import { DH } from '../../../utils/DataHook';
import { CardNetwork, CardNetworkRedesigned, CardNetworksListRedesigned, Container } from './elements';
import s from './styles.scss';

interface CardNumberViewProps extends Omit<GeneralFieldProps, 'onChange'> {
  cardNetwork: CreditCardNetwork;
  cardNetworkBrandsList: CreditCardNetwork[];

  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onChange: ChangeEventHandler<HTMLInputElement>;
}

export const CardNumberView: FC<CardNumberViewProps> = memo(
  forwardRef((props: CardNumberViewProps, ref: RefObject<HTMLInputElement>) => {
    const {
      cardNetwork,
      cardNetworkBrandsList,
      value,
      isValid,
      isErrorCanBeShown,
      isTouched,
      isFinished,
      customInput,
      onKeyDown,
      onChange,
      onBlur,
      onFocus,
      onLabelClick,
    } = props;
    const deviceType = useContext(DeviceTypeContext);
    const providerId: PaymentProvider = useContext(PaymentProviderContext);
    const formattedValue: string = formatCardNumber(value);

    if (!customInput) {
      return (
        <>
          <div className={s.cardNetworksListOnTop}>
            <CardNetworksListRedesigned cardNetworks={cardNetworkBrandsList} />
          </div>

          <div data-hook={DH.Field.Container.Number}>
            <FieldInput
              ref={ref}
              label={t('card.number')}
              isTouched={isTouched}
              isValid={isValid}
              isErrorCanBeShown={isErrorCanBeShown}
              errorMessage={getFieldErrorMessage({ fieldName: FieldName.Number, isFinished, isEmpty: !value })}
              type="tel"
              fieldName={FieldName.Number}
              autoComplete="cc-number"
              value={formattedValue}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
              required
              pattern="[\d\s]*"
              placeholder={t('card.number.placeholder.redesign')}
              suffix={
                cardNetwork ? (
                  <CardNetworkRedesigned cardNetwork={cardNetwork} />
                ) : (
                  !formattedValue && (
                    <div className={s.cardNetworksListInInput}>
                      <CardNetworksListRedesigned cardNetworks={cardNetworkBrandsList} />
                    </div>
                  )
                )
              }
              onChangeRaw={onChange}
            />
          </div>
        </>
      );
    }
    return (
      <Container>
        <FieldLabel fieldName={FieldName.Number} text={t('card.number')} onClick={onLabelClick} />
        <FieldInput
          customInput
          ref={ref}
          fieldName={FieldName.Number}
          placeholder={t('card.number.placeholder')}
          type="tel"
          pattern="[\d\s]*"
          isValid={isValid}
          isTouched={isTouched}
          required
          name="cardnumber"
          autoComplete="cc-number"
          value={formattedValue}
          onChangeRaw={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        {cardNetwork && deviceType === 'desktop' && (
          <CardNetwork cardNetwork={cardNetwork} forceRightPosition={providerId === PaymentProvider.Square} />
        )}
        {isTouched && !isValid && <FieldErrorMessage fieldName={FieldName.Number} />}
      </Container>
    );
  }),
);
CardNumberView.displayName = 'CardNumberView';
